/**
 * SEO-template funnel handoff (marketing page → app).
 *
 * A template landing page CTA can't create the project itself — the visitor
 * is usually unauthenticated and about to round-trip through /login (and
 * possibly a full OAuth redirect). So the CTA stashes the chosen template
 * here, and CoreProvider's boot consumes it once a session exists: create
 * the seeded project, land the user inside it.
 *
 * The stash is deliberately short-lived (2h): someone who parks on the
 * login page and comes back days later should get the normal app, not a
 * surprise project from a long-forgotten click.
 */

const KEY = 'draftlize.pending_template';
const TTL_MS = 2 * 60 * 60 * 1000;

export interface PendingTemplate {
  slug: string;
  lang: 'en' | 'zh';
}

export function stashPendingTemplate(slug: string, lang: 'en' | 'zh'): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ slug, lang, ts: Date.now() }));
  } catch {
    /* storage unavailable (private mode etc.) — the CTA still lands on
       /login; the visitor just gets the normal post-login app. */
  }
}

// Claim memo: React StrictMode double-runs CoreProvider's boot effect, and
// a read-and-wipe localStorage take would hand the stash to exactly one of
// the two runs — the other would fall through to the default project and
// race the template landing. Memoizing the claim at module scope makes
// every boot run within this page load see the SAME answer, so they all
// take the template branch (and share one create, see below) or none do.
let claimed: PendingTemplate | null | undefined;

/** Read-and-wipe, memoized per page load. Returns null when absent,
 *  malformed, or expired. */
export function claimPendingTemplate(): PendingTemplate | null {
  if (claimed !== undefined) return claimed;
  claimed = readAndWipe();
  return claimed;
}

function readAndWipe(): PendingTemplate | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
    if (raw !== null) window.localStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { slug?: unknown; lang?: unknown; ts?: unknown };
    if (typeof v.slug !== 'string' || typeof v.ts !== 'number') return null;
    if (Date.now() - v.ts > TTL_MS) return null;
    return { slug: v.slug, lang: v.lang === 'zh' ? 'zh' : 'en' };
  } catch {
    return null;
  }
}
